/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import PropTypes from 'prop-types';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import defaultsDeep from 'lodash/defaultsDeep';
import isEmpty from 'lodash/isEmpty';
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import getComponent from '../../../utils/getComponent';
import { getPrefilledSolutionValue } from '../../../utils/getPrefilledSolutionValue';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  transformWildcardsToText,
  getWildcardData,
} from '../../../utils/wildcards';
import DotComponent from '../../ui/DotComponent';
import OpenEnquiryFormButton from '../../ui/OpenEnquiryFormButton';
import Select, { mapDropdownValues } from '../../ui/Form/Dropdown';
import Spacer from '../../ui/Spacer';
import LottieAnimation, {
  getAmountAnimationIdFromIndex,
} from '../../ui/LottieAnimation';
import { componentHasStickyLabel } from '../StickyBar';
import {
  titleCss,
  descriptionCss,
  contentCss,
  formContainerCss,
  selectsContainerCss,
  selectCss,
  containerCss,
  imagesColumnCss,
  marginCss,
} from './index.styles';

export const defaultData = {
  ProtonName: {
    value: '',
  },
  Headline: {
    value: '',
  },
  DescriptionText: {
    value: '',
  },
  NumberOfPeople: [],
  Availability: [],
  Cta: {
    value: { label: '', url: '#' },
  },
  formImage: {
    value: {
      alt: 'graphic',
      src: 'https://i.imgur.com/UO79WOR.png',
    },
  },
};

export class WorkspaceEditor extends SafePureComponent {
  constructor(props) {
    super(props);
    this.t = props.t;
    this.hasStickyLabel = componentHasStickyLabel(props);
    this.animationRef = React.createRef();
    this.wildcards = getWildcardData(this.props.sitecoreContext);

    this.state = {
      currentAnimationId: null,
      GeosolutionHeader: null,
      howManyPeopleValue: '',
      isNumberOfPeopleSelected: false,
      location: '',
      moveInDateValue: '',
    };
  }

  componentDidMount() {
    const { sitecoreContext } = this.props;

    this.setState({
      GeosolutionHeader: getComponent(sitecoreContext, 'GeosolutionHeader'),
      currentAnimationId: getAmountAnimationIdFromIndex(0),
    });
  }

  componentDidUpdate(previousProp, previousState) {
    if (previousState.GeosolutionHeader !== this.state.GeosolutionHeader) {
      const geoSolutionName = get(
        this.state.GeosolutionHeader,
        'fields.GeoName.value',
        '',
      );

      this.setState({ location: geoSolutionName });
    }
  }

  renderGetInTouchCTA(componentName) {
    const { location, howManyPeopleValue, moveInDateValue } = this.state;

    const openEnquiryFormButtonProps = {
      prefilledValues: {
        Location: location,
        HowManyPeople: howManyPeopleValue,
        MoveInDate: moveInDateValue,
        Solution: getPrefilledSolutionValue(this.props.sitecoreContext),
      },
    };

    return (
      <OpenEnquiryFormButton
        {...openEnquiryFormButtonProps}
        componentName={componentName}
      />
    );
  }

  onChangeNumberOfPeople = (_event, value, index) => {
    const NumberOfPeopleNextState = this.state.isNumberOfPeopleSelected
      ? {
          currentAnimationId: getAmountAnimationIdFromIndex(index),
          howManyPeopleValue: value,
        }
      : {
          currentAnimationId: getAmountAnimationIdFromIndex(index - 1),
          howManyPeopleValue: value,
          isNumberOfPeopleSelected: true,
        };
    this.setState(NumberOfPeopleNextState);
  };

  onChangeMoveInDate = (_event, value) => {
    this.setState({
      moveInDateValue: value,
    });
  };

  /**
   * This function inject the wildcards values and transform that values to be used in the Headline and Description
   * @param  {Object} fields dictionary with the component values from sitecore
   * @return {React.Node} Return the headline, description and link of the component
   */
  renderTitleAndDescription = fields => {
    const { t } = this.props;
    const contextFields = get(this.props, 'sitecoreContext.route.fields', {});
    const componentName = get(this.props, 'rendering.componentName');
    const basicWildcardValuesToTransform = {
      componentName,
      contextFields,
      fields,
      t,
      wildcards: this.wildcards,
    };
    const Headline = {
      value: transformWildcardsToText({
        ...basicWildcardValuesToTransform,
        text: get(fields, 'Headline.value', ''),
      }),
    };

    const Description = {
      value: transformWildcardsToText({
        ...basicWildcardValuesToTransform,
        text: get(fields, 'DescriptionText.value', ''),
      }),
    };
    return (
      <React.Fragment>
        <h2 css={titleCss}>
          <Text field={Headline} />
          <DotComponent />
        </h2>
        <p css={descriptionCss}>
          <Text field={Description} />
        </p>
      </React.Fragment>
    );
  };

  safeRender() {
    const fields = defaultsDeep(this.props.fields, defaultData);
    const { currentAnimationId } = this.state;
    const componentName = get(this.props, 'rendering.componentName', '');

    return (
      <Spacer
        customCss={containerCss}
        marginCss={marginCss}
        containerProps={{
          'data-sticky-id':
            this.hasStickyLabel && get(this.props, 'rendering.uid'),
        }}
      >
        <div css={contentCss}>
          {this.renderTitleAndDescription(fields)}
          <div css={formContainerCss}>
            <form css={selectsContainerCss}>
              {!isEmpty(fields.NumberOfPeople) && (
                <Select
                  id="HowManyPeople"
                  css={selectCss}
                  options={mapDropdownValues(fields.NumberOfPeople)}
                  onChangeProp={this.onChangeNumberOfPeople}
                  placeholder={this.t('HowManyPeoplePlaceholder')}
                />
              )}

              {!isEmpty(fields.Availability) && (
                <Select
                  id="MoveInDate"
                  css={selectCss}
                  options={mapDropdownValues(fields.Availability)}
                  onChangeProp={this.onChangeMoveInDate}
                  placeholder={this.t('MoveInDatePlaceholder')}
                />
              )}

              {this.renderGetInTouchCTA(componentName)}
            </form>
          </div>
        </div>
        <div css={imagesColumnCss}>
          <LottieAnimation height={300} nextAnimationId={currentAnimationId} />
        </div>
      </Spacer>
    );
  }
}

WorkspaceEditor.displayName = 'WorkspaceEditor';

WorkspaceEditor.defaultProps = { fields: defaultData, t: () => {} };

WorkspaceEditor.propTypes = {
  fields: PropTypes.shape({
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),
    DescriptionText: PropTypes.shape({
      value: PropTypes.string,
    }),
    NumberOfPeople: PropTypes.array,
    Availability: PropTypes.array,
    Cta: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  t: PropTypes.func,
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
)(WorkspaceEditor);
